import { coreApi } from '$api'
import {
  ClockinResponse,
  IClockinHistory,
  IClockinResponse,
  IShiftResponse,
  transformClockinHistoryOnGet,
} from '$models'
import { ShiftType } from '$constants'
import { singletonQueryClient } from '$utils'
import { Dayjs } from 'dayjs'

enum QueryKeys {
  getClockins = 'clockins',
  getClockinHistories = 'clockin-histories',
  getClockinsExportFile = 'clockins-export-file',
}

/**
 * Get all Clockins
 * @param startDateTime - include all the clockins from a specific time
 * @param endDateTime - include all the clockins until a specific time
 * @param includeInactive - include the inactive ones
 * @param shiftTypesToInclude - optional list of shift types to filter by (e.g. ShiftType.Clockin)
 * @param staffMemberIds - the ids of the staff members to get the clocks for
 * @param shouldTruncateShifts - Denotes if we should truncate the shifts or not
 * @param filterByPayrollCompany - when true, filters staff by payroll company and includes cross-venue shifts
 * @returns Promise<IShiftResponse[]>
 */
export const getClockins = async (
  startDateTime: Dayjs,
  endDateTime: Dayjs,
  includeInactive: boolean = false,
  shiftTypesToInclude?: ShiftType[],
  staffMemberIds?: string[],
  shouldTruncateShifts: boolean = true,
  includeCrossVenueShifts: boolean = false,
  filterByPayrollCompany: boolean = false
): Promise<IShiftResponse[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.getClockins,
      startDateTime,
      endDateTime,
      includeInactive,
      shiftTypesToInclude,
      staffMemberIds,
      shouldTruncateShifts,
      includeCrossVenueShifts,
      filterByPayrollCompany,
    ],
    queryFn: async ({ signal }) => {
      const clockins = await coreApi.getClockins(
        startDateTime,
        endDateTime,
        includeInactive,
        undefined,
        shiftTypesToInclude,
        staffMemberIds,
        shouldTruncateShifts,
        includeCrossVenueShifts,
        filterByPayrollCompany,
        signal
      )

      return clockins
    },
  })
}

/**
 * Update clockin detail
 * @param clockinDetail - clockinDetail to update
 * @returns Promise<ClockinDetail>
 */
export const update = async (
  clockinDetail: IClockinResponse
): Promise<IClockinResponse> => {
  const updatedClockinDetail = await coreApi.updateClockin(
    clockinDetail.id!,
    clockinDetail as ClockinResponse
  )

  return updatedClockinDetail
}

export const add = async (
  clockinDetail: IClockinResponse
): Promise<IClockinResponse> => {
  const newClockinDetail = await coreApi.addClockin(
    clockinDetail as ClockinResponse
  )

  return newClockinDetail
}

export const getClockinHistory = async (
  operation: string | undefined,
  startTime: Dayjs | undefined,
  endTime: Dayjs | undefined,
  clockinId: string | undefined,
  page: number,
  pageSize: number,
  sort: string | undefined
): Promise<IClockinHistory[]> => {
  return singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.getClockinHistories,
      clockinId,
      operation,
      startTime,
      endTime,
      page,
      pageSize,
      sort,
    ],
    queryFn: async () => {
      const clockinHistories = await coreApi.getClockinHistories(
        operation,
        startTime,
        endTime,
        clockinId,
        page,
        pageSize,
        sort
      )

      return clockinHistories.result.map(transformClockinHistoryOnGet)
    },
  })
}

/**
 * Export clockins to a payroll file
 * @param startDateTime - start of the export period
 * @param endDateTime - end of the export period
 * @param format - the payroll system format to export as
 * @param filterByPayrollCompany - when true, only include staff whose payroll company matches the current company
 * @param includeCrossVenueShifts - when true, includes shifts from other venues where staff are assigned
 * @returns the download URL for the exported file
 */
export const getClockinsExportFile = async (
  startDateTime: Dayjs,
  endDateTime: Dayjs,
  format: string,
  filterByPayrollCompany: boolean = false,
  includeCrossVenueShifts: boolean = false
): Promise<string> => {
  const result = await singletonQueryClient.fetchQuery({
    queryKey: [
      QueryKeys.getClockinsExportFile,
      startDateTime,
      endDateTime,
      format,
      filterByPayrollCompany,
      includeCrossVenueShifts,
    ],
    queryFn: async ({ signal }) => {
      return coreApi.getClockinsExportFile(
        startDateTime,
        endDateTime,
        format,
        filterByPayrollCompany,
        includeCrossVenueShifts,
        signal
      )
    },
  })

  return result.location!
}
